'use client';
import { forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** Use a floating label that lifts on focus / when filled. */
  floatingLabel?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      icon,
      iconRight,
      floatingLabel = false,
      className,
      id,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const inputId = id || autoId;
    const hasWrapper = !!(label || error || hint || icon || iconRight);

    const baseInput = (
      <input
        ref={ref}
        id={inputId}
        placeholder={floatingLabel ? ' ' : placeholder}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        className={cn(
          'peer w-full h-11 rounded-md bg-surface text-ink text-[15px]',
          'border border-border hover:border-ink-muted',
          'focus:outline-none focus:border-cta focus:ring-2 focus:ring-cta/25',
          'transition-colors duration-200 ease-premium',
          'placeholder:text-ink-muted disabled:opacity-50 disabled:cursor-not-allowed',
          icon ? 'pl-10' : 'pl-3.5',
          iconRight ? 'pr-10' : 'pr-3.5',
          error && 'border-error focus:border-error focus:ring-error/25',
          className,
        )}
        {...props}
      />
    );

    if (!hasWrapper && !floatingLabel) return baseInput;

    return (
      <div className="flex flex-col gap-1.5">
        {label && !floatingLabel && (
          <label
            htmlFor={inputId}
            className="text-[12px] font-semibold uppercase tracking-[0.04em] text-ink-secondary"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted [&>svg]:h-4 [&>svg]:w-4"
            >
              {icon}
            </span>
          )}
          {baseInput}
          {floatingLabel && label && (
            <label
              htmlFor={inputId}
              className={cn(
                'pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 origin-left',
                'text-ink-muted text-[15px] transition-all duration-200 ease-premium',
                'peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:font-semibold peer-focus:text-cta peer-focus:tracking-[0.04em] peer-focus:uppercase',
                'peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-ink-secondary peer-[:not(:placeholder-shown)]:tracking-[0.04em] peer-[:not(:placeholder-shown)]:uppercase',
                icon && 'left-10',
              )}
            >
              {label}
            </label>
          )}
          {iconRight && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-muted [&>svg]:h-4 [&>svg]:w-4">
              {iconRight}
            </span>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-error font-medium">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-xs text-ink-muted">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
